import React, { useMemo } from "react";
import { DateHeader, IntervalRenderer, ReactCalendarTimelineProps, SidebarHeader, Timeline, TimelineHeaders, TimelineItemBase } from "react-calendar-timeline";

import moment from "moment";

import { formatLocalizedMonth, getLanguageFromLocalStorage, getVerticalLineClassNamesForTime, useGetFarmReportingYear, useGetFarms } from "utils";

import { Flex, Tag } from "components";

import { CustomTimelineGroup, getTimelineDefaultStartAndEnd } from "pages/app/field-timeline/utils/TimelineUtils";

export type TimelineCalendarProps = ReactCalendarTimelineProps<TimelineItemBase<number>, CustomTimelineGroup> & {
  className?: string;
  topHeaderExtras?: React.ReactNode;
  highlightCurrentYear?: boolean;
  tagText?: string;
  screenSize?: "small" | "medium" | "large";
  isSimulationView?: boolean;
  reportingYear: number;
};
export const TimelineCalendar = <T,>({
  className = "",
  highlightCurrentYear,
  topHeaderExtras,
  tagText,
  screenSize,
  isSimulationView,
  reportingYear,
  ...props
}: TimelineCalendarProps) => {
  const language = getLanguageFromLocalStorage({ forDates: true });
  const { currentFarm } = useGetFarms();
  const carbonProgram = currentFarm?.carbonProgram ?? null;
  const { data: farmReportingYearData } = useGetFarmReportingYear(currentFarm?.id || null);

  // set the language
  moment.locale(language);

  const firstEntryYear = Math.min(...props.items.filter((item) => item.start_time).map((item) => moment(item.start_time).year()));
  const startLimit = moment(`${firstEntryYear}-01-01`).subtract(3, "years").startOf("year").valueOf();
  const endLimit = moment().add(5, "years").endOf("year").valueOf();

  const handleTimeChange: ReactCalendarTimelineProps<TimelineItemBase<number>>["onTimeChange"] = (visibleTimeStart, visibleTimeEnd, updateScrollCanvas) => {
    if (visibleTimeStart < startLimit) {
      updateScrollCanvas(startLimit, startLimit + (visibleTimeEnd - visibleTimeStart));
    } else if (visibleTimeEnd > endLimit) {
      updateScrollCanvas(endLimit - (visibleTimeEnd - visibleTimeStart), endLimit);
    } else {
      updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
    }
  };

  const horizontalLineClassNamesForGroup = (group: CustomTimelineGroup) => (group.isLastOfGroup ? [] : ["dotted-lines-border"]);

  const timelineStartEnd = useMemo(
    () => getTimelineDefaultStartAndEnd(farmReportingYearData, carbonProgram, screenSize),
    [farmReportingYearData, carbonProgram, screenSize]
  );
  const timelineZoomLevel = timelineStartEnd.end - timelineStartEnd.start;

  return (
    <Timeline
      // hack to force the component to re-render when the "screenSize" prop changes, we need this to automatically
      // update the defaultTimeStart and defaultTimeEnd without needing to switch to a fully controlled state
      key={`${screenSize}-${farmReportingYearData}`}
      className={`timeline-calendar ${className}`}
      // very ad-hoc value to account for impact panel width and show exactly the end of 5th future year
      rightSidebarWidth={-115}
      // @ts-expect-error this is impenetrable
      defaultTimeStart={timelineStartEnd.start}
      // @ts-expect-error this is impenetrable
      defaultTimeEnd={timelineStartEnd.end}
      // This allows us to control the zoom level (in this case there is no zooming in/out).
      minZoom={timelineZoomLevel}
      maxZoom={timelineZoomLevel}
      // @ts-expect-error this is impenetrable
      verticalLineClassNamesForTime={
        isSimulationView ? () => ["simulations"] : getVerticalLineClassNamesForTime(!!highlightCurrentYear, reportingYear, currentFarm?.carbonProgram)
      }
      horizontalLineClassNamesForGroup={horizontalLineClassNamesForGroup}
      onTimeChange={handleTimeChange}
      {...props}
    >
      <TimelineHeaders className="timeline-calendar-header">
        <SidebarHeader>{({ ...props }) => <div className="sidebar-header-left" {...props} />}</SidebarHeader>
        <DateHeader
          unit="year"
          className="header-years"
          labelFormat="YYYY"
          // @ts-expect-error this is impenetrable
          intervalRenderer={({ getIntervalProps, intervalContext }: IntervalRenderer<T>) => {
            const isCurrentYear = intervalContext.intervalText === moment().format("YYYY");
            return (
              <div className="content" {...getIntervalProps()}>
                <Flex align="center" justify="flex-start" gap="0.5rem">
                  {intervalContext.intervalText}
                  {isCurrentYear && tagText && <Tag colour="yellow" text={tagText} />}
                  {topHeaderExtras}
                </Flex>
              </div>
            );
          }}
        />
        <DateHeader
          unit="month"
          className="header-months"
          labelFormat="MMMM"
          // @ts-expect-error this is impenetrable
          intervalRenderer={({ getIntervalProps, intervalContext }: IntervalRenderer<T>) => {
            const month = intervalContext.interval.startTime.month();
            const isFirstMonthOfYear = month === 0;

            return (
              <div className={`content ${isFirstMonthOfYear && "first-month"} `} {...getIntervalProps()}>
                <Flex align="center" justify="center">
                  {formatLocalizedMonth(month).slice(0, 3)}
                </Flex>
              </div>
            );
          }}
        />
      </TimelineHeaders>
    </Timeline>
  );
};
